import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { useI18n } from "@/i18n/I18nContext";
import { text } from "@/lib/memberDerive";
import { readVisibility, VISIBILITY } from "@/lib/v3Pass";
import { shortName, interestText, normalizeBlocks, initialsOf } from "@/lib/v3People";
import { useCore } from "../useCore";
import { useMemberProfile } from "../useMemberProfile";
import { runAction } from "../actions";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, Unavailable, ErrorNote, ConfirmDialog } from "../components/kit";
import { V3Title, V3Columns, V3Actions, DetailRow, DetailList, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { AreaCard, ResponsibleCard, SearchField, ResultTable, ResultRow } from "../components/v3app";
import { ProfileHead } from "../components/v3people";
import { usePerson, PersonMissing } from "./PersonDetail";

const VIS_TEXT = Object.fromEntries(VISIBILITY);

// M51: own profile as other members see it; per-field visibility only when the Core stores it (C.2).
export function OwnMemberView() {
  const { t } = useI18n();
  const p = useMemberProfile();
  if (p.error) return <ConnectionError onRetry={p.load} />;
  if (!p.data) return <Loading />;
  const prof = p.data.profile || {};
  const vis = readVisibility(prof);
  const name = shortName(prof.name);
  const shown = (k, value) => (vis.values[k] === "members" ? value || "Nicht angegeben" : `Nicht für alle Mitglieder sichtbar (${VIS_TEXT[vis.values[k]]})`);
  return (
    <div data-m="M51" data-testid="own-member-view">
      <V3Title title="Für Mitglieder sichtbares Profil" intro="Diese Vorschau zeigt deine für Mitglieder sichtbaren Angaben." testid="own-member-view-title" />
      <V3Columns layout="app" aside={<><AreaCard text={name || "Dein Profil"} testid="own-member-view-area" /><ResponsibleCard /></>} main={<>
        <ProfileHead name={vis.values.name === "members" ? name : "Name ausgeblendet"} place={vis.values.city === "members" ? prof.city : ""} color={prof.avatar_color} testid="own-member-view-head" />
        <DetailList>
          <DetailRow label="Anzeigename" value={shown("name", name)} testid="own-member-view-name" />
          <DetailRow label="Stadt" value={shown("city", prof.city)} testid="own-member-view-city" />
          <DetailRow label="Über mich" value={shown("bio", text(prof.bio))} testid="own-member-view-bio" />
          <DetailRow label="Interessen" value={shown("interests", interestText(prof.interests, (k) => t(`member.categories.${k}`)))} testid="own-member-view-interests" />
        </DetailList>
        {!vis.supported && <p className="mt-[12px] text-[13px] text-v3-muted" data-testid="own-member-view-note">Sichtbarkeit je Feld ist noch nicht einstellbar; der Core zeigt diese Angaben derzeit allen angemeldeten Mitgliedern{prof.prefs?.privacy?.discoverable === false ? ", aber nicht bei „Menschen entdecken“" : ""}.</p>}
        <V3Actions>
          <Link to="/profil" data-testid="own-member-view-edit" className={V3_PRIMARY}>Profil bearbeiten</Link>
          <Link to="/profil" data-testid="own-member-view-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
      </>} />
    </div>
  );
}

// M52: shared circles need GET /member/directory/{id}/shared-groups (C.3); hidden memberships are never guessed.
export function SharedGroups() {
  const { id } = useParams();
  const { dir, person } = usePerson(id);
  if (dir.error) return <ConnectionError onRetry={dir.reload} />;
  if (person === undefined) return <Loading />;
  if (!person) return <PersonMissing />;
  return (
    <div data-m="M52" data-testid="shared-groups">
      <V3Title title="Gemeinsame Kreise im Profil" intro="Hier erscheinen nur Kreise, deren Mitgliedschaft sichtbar sein darf." testid="shared-groups-title" />
      <ResultTable testid="shared-groups-table" label="Gemeinsame Kreise">
        <ResultRow initial="G" title="Gemeinsame Kreise" context="Noch nicht verfügbar" action="" testid="shared-groups-list" />
        <ResultRow initial="W" title="Weitere Mitgliedschaften" context="Nicht sichtbar" action="" testid="shared-groups-hidden" />
        <ResultRow initial="K" title="Kreis öffnen" context="Aktuelle Zugriffsrechte gelten weiterhin" to="/kreise" testid="shared-groups-open" />
      </ResultTable>
      <div className="mt-[14px]"><Unavailable testid="shared-groups-unavailable">Der gemeinsame Core liefert noch keine gemeinsamen Kreise mit {shortName(person.name)}. Es wird nichts geraten und keine Mitgliedschaft offengelegt.</Unavailable></div>
      <V3Actions className="mt-[21px]">
        <Link to="/kreise" data-testid="shared-groups-circles" className={V3_PRIMARY}>Kreis ansehen</Link>
        <Link to={`/menschen/${encodeURIComponent(person.id)}`} data-testid="shared-groups-back" className={V3_SECONDARY}>Zurück</Link>
      </V3Actions>
    </div>
  );
}

// M112: own blocks from GET /member/blocks; lifting one needs a confirmation and DELETE /member/blocks/{id}.
export function BlockedContacts() {
  const res = useCore("/member/blocks", normalizeBlocks);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState("");
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const list = (res.data || []).filter((b) => !q.trim() || b.name.toLocaleLowerCase("de").includes(q.trim().toLocaleLowerCase("de")));
  const chosen = list.find((b) => b.id === pick);
  const unblock = async () => {
    setBusy(true); setError("");
    const err = await runAction("delete", `/member/blocks/${encodeURIComponent(chosen.id)}`, "Die Blockierung wurde nicht aufgehoben");
    setBusy(false); setAsk(false);
    if (err) { setError(err); return; }
    toast.success("Blockierung aufgehoben."); setPick(""); res.reload();
  };
  return (
    <div data-m="M112" data-testid="blocked-contacts">
      <V3Title title="Blockierte Kontakte verwalten" intro="Hier kannst du deine eigenen Blockierungen prüfen." />
      <SearchField id="blocked-q" label="Blockierte Personen durchsuchen" placeholder="Name, Thema oder Vorgang suchen …" value={q} onChange={setQ} testid="blocked-search" />
      <div className="mt-[17px]">
        {res.error ? <ConnectionError onRetry={res.reload} /> : !res.data ? <Loading /> : (
          <ResultTable testid="blocked-table" label="Blockierungen">
            {list.map((b) => <ResultRow key={b.id} initial={initialsOf(b.name)} title={shortName(b.name)} context="Blockiert · Kontaktmöglichkeiten eingeschränkt"
              onOpen={() => setPick(pick === b.id ? "" : b.id)} expanded={pick === b.id} action={pick === b.id ? "Ausgewählt" : "Auswählen"} testid={`blocked-${b.id}`} />)}
            {!list.length && <ResultRow initial="–" title={q ? "Keine Treffer" : "Niemand blockiert"} context={q ? "Passe die Suche an." : "Blockieren kannst du im Profil einer Person."} action="" testid="blocked-empty" />}
            <ResultRow initial="B" title="Blockierung aufheben" context="Nur nach deiner Bestätigung" action="" testid="blocked-rule-confirm" />
            <ResultRow initial="M" title="Vorhandene Meldung" context="Wird dadurch nicht zurückgezogen" action="" testid="blocked-rule-report" />
            <ResultRow initial="F" title="Private Freigaben" context="Gesondert verwalten" to="/access-pass/freigaben" testid="blocked-rule-grants" />
          </ResultTable>
        )}
      </div>
      {error && <div className="mt-[12px]"><ErrorNote testid="blocked-error">{error}</ErrorNote></div>}
      <V3Actions className="mt-[21px]">
        <button type="button" disabled={!chosen || busy} aria-describedby={chosen && !busy ? undefined : "blocked-review-hint"} onClick={() => setAsk(true)} data-testid="blocked-review" className={V3_PRIMARY}>Blockierung prüfen</button>
        {(!chosen || busy) && <span id="blocked-review-hint" className="sr-only">{busy ? "Wird gerade verarbeitet." : "Wähle zuerst eine blockierte Person in der Liste aus."}</span>}
      </V3Actions>
      <ConfirmDialog open={ask && !!chosen} onOpenChange={setAsk} testid="blocked-unblock-dialog" busy={busy} confirmLabel="Blockierung aufheben" onConfirm={unblock}
        title={`Blockierung von ${chosen ? shortName(chosen.name) : ""} aufheben?`}
        description="Die Person kann dir danach wieder Kontaktanfragen senden, wenn du Anfragen erlaubst. Eine frühere Meldung bleibt bestehen; ein früheres Gespräch wird nicht wiederhergestellt." />
    </div>
  );
}
